/**
 * Service d'alimentation organique du catalogue matériel.
 *
 * Ce module encapsule la logique qui transforme les données d'un BL
 * (Bon de Livraison) en entrées du catalogue matériel et de l'historique des prix.
 *
 * Utilisé en :
 * - Fallback si l'Edge Function n'a pas pu alimenter la BDD
 * - Mode principal quand l'utilisateur crée un BL manuellement (sans PDF)
 */

import { supabase } from './supabase';

// ─── TYPES ──────────────────────────────────────────────────────────────────

export interface BLLineForCatalog {
  reference: string;
  designation: string;
  quantity: number;
  unit: string;
  unit_price: number;
  material_id?: string | null; // Si déjà résolu par l'Edge Function
}

export interface CatalogFeedResult {
  materialsCreated: number;
  materialsUpdated: number;
  pricesRecorded: number;
  materialIdMap: Map<string, string>; // reference → material_id
  errors: string[];
}

// ─── FONCTIONS PRINCIPALES ──────────────────────────────────────────────────

/**
 * UPSERT un matériel dans le catalogue organique.
 * Si la référence existe → met à jour la désignation.
 * Si elle n'existe pas → crée une nouvelle entrée.
 *
 * @returns material_id (existant ou nouveau), ou null en cas d'erreur
 */
export async function upsertMaterial(
  reference: string,
  designation: string,
  unit: string = 'pce',
  brand?: string
): Promise<string | null> {
  if (!reference || reference.trim() === '') return null;

  const { data, error } = await supabase
    .from('material_catalog')
    .upsert(
      {
        reference: reference.trim().toUpperCase(),
        designation: designation.trim(),
        unit: (unit || 'pce').toLowerCase(),
        brand: brand?.trim() || null,
        is_active: true,
        updated_at: new Date().toISOString()
      },
      { onConflict: 'reference' }
    )
    .select('id')
    .single();

  if (error) {
    console.error(`[CatalogService] Erreur upsert material "${reference}":`, error.message);
    return null;
  }

  return data?.id || null;
}

/**
 * UPSERT un prix fournisseur pour un matériel donné.
 * Le trigger BDD `record_price_history()` insère automatiquement dans `price_history`.
 *
 * @returns true si succès, false sinon
 */
export async function upsertSupplierPrice(
  materialId: string,
  supplierId: string,
  netPrice: number,
  source: string = 'pdf_extraction'
): Promise<boolean> {
  if (!materialId || !supplierId || netPrice <= 0) return false;

  const today = new Date().toISOString().split('T')[0];

  const { error } = await supabase
    .from('supplier_prices')
    .upsert(
      {
        material_id: materialId,
        supplier_id: supplierId,
        net_price: netPrice,
        effective_date: today,
        source: source,
        is_active: true,
        updated_at: new Date().toISOString()
      },
      { onConflict: 'material_id,supplier_id,effective_date' }
    );

  if (error) {
    console.error(`[CatalogService] Erreur upsert prix [${materialId}]:`, error.message);
    return false;
  }

  return true;
}

/**
 * Alimente le catalogue organique à partir des lignes d'un BL.
 * Pour chaque ligne avec une référence :
 * 1. UPSERT dans material_catalog → récupérer material_id
 * 2. UPSERT dans supplier_prices → le trigger enregistre dans price_history
 *
 * Tolérante aux erreurs : continue si une ligne échoue.
 *
 * @returns Résumé avec compteurs et mapping reference → material_id
 */
export async function feedCatalogFromBL(
  supplierId: string,
  lines: BLLineForCatalog[]
): Promise<CatalogFeedResult> {
  const result: CatalogFeedResult = {
    materialsCreated: 0,
    materialsUpdated: 0,
    pricesRecorded: 0,
    materialIdMap: new Map(),
    errors: []
  };

  // Filtrer les lignes avec référence et normaliser
  const validLines = lines.filter(l => l.reference && l.reference.trim() !== '');
  if (validLines.length === 0) return result;

  const refs = validLines.map(l => l.reference.trim().toUpperCase());
  const today = new Date().toISOString().split('T')[0];

  // ── REQUÊTE 1 : Récupérer les références déjà existantes en une seule requête ──
  const { data: existingMaterials } = await supabase
    .from('material_catalog')
    .select('id, reference')
    .in('reference', refs);

  const existingRefSet = new Set((existingMaterials || []).map(m => m.reference));

  // ── REQUÊTE 2 : UPSERT batch de tous les matériaux ──
  const materialsToUpsert = validLines.map(l => ({
    reference: l.reference.trim().toUpperCase(),
    designation: l.designation.trim(),
    unit: (l.unit || 'pce').toLowerCase(),
    is_active: true,
    updated_at: new Date().toISOString()
  }));

  const { data: upsertedMaterials, error: upsertError } = await supabase
    .from('material_catalog')
    .upsert(materialsToUpsert, { onConflict: 'reference' })
    .select('id, reference');

  if (upsertError) {
    console.error('[CatalogService] Erreur batch upsert materials:', upsertError.message);
    result.errors.push('Erreur lors de la mise à jour du catalogue');
    return result;
  }

  // Construire le mapping reference → material_id
  for (const mat of (upsertedMaterials || [])) {
    result.materialIdMap.set(mat.reference, mat.id);
    if (existingRefSet.has(mat.reference)) {
      result.materialsUpdated++;
    } else {
      result.materialsCreated++;
    }
  }

  // ── REQUÊTE 3 : UPSERT batch de tous les prix ──
  const pricesToUpsert = validLines
    .filter(l => l.unit_price > 0 && supplierId)
    .map(l => {
      const ref = l.reference.trim().toUpperCase();
      const materialId = result.materialIdMap.get(ref);
      if (!materialId) return null;
      return {
        material_id: materialId,
        supplier_id: supplierId,
        net_price: l.unit_price,
        effective_date: today,
        source: 'pdf_extraction' as const,
        is_active: true,
        updated_at: new Date().toISOString()
      };
    })
    .filter((p): p is NonNullable<typeof p> => p !== null);

  if (pricesToUpsert.length > 0) {
    const { error: priceError } = await supabase
      .from('supplier_prices')
      .upsert(pricesToUpsert, { onConflict: 'material_id,supplier_id,effective_date' });

    if (priceError) {
      console.error('[CatalogService] Erreur batch upsert prix:', priceError.message);
      result.errors.push('Erreur lors de l\'enregistrement des prix');
    } else {
      result.pricesRecorded = pricesToUpsert.length;
    }
  }

  return result;
}

/**
 * Upload un fichier PDF vers Supabase Storage (bucket bl-pdfs).
 *
 * @returns { filePath, publicUrl } ou null en cas d'erreur
 */
export async function uploadPdfToStorage(
  file: File,
  userId: string
): Promise<{ filePath: string; publicUrl: string } | null> {
  const timestamp = Date.now();
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const filePath = `${userId}/${timestamp}_${safeName}`;

  const { error } = await supabase
    .storage
    .from('bl-pdfs')
    .upload(filePath, file, {
      contentType: 'application/pdf',
      upsert: false
    });

  if (error) {
    console.error('[CatalogService] Erreur upload PDF:', error.message);
    return null;
  }

  // Générer une URL signée (valide 1 an)
  const { data: signedUrl } = await supabase
    .storage
    .from('bl-pdfs')
    .createSignedUrl(filePath, 365 * 24 * 60 * 60);

  return {
    filePath,
    publicUrl: signedUrl?.signedUrl || filePath
  };
}

/** Type de retour commun des fonctions d'extraction PDF */
export type ExtractPdfResult = {
  success: boolean;
  extracted?: {
    supplier_name: string;
    bl_number: string;
    bl_date: string;
    project_code?: string | null;
    lines: (BLLineForCatalog & { material_id?: string | null })[];
    total_ht: number | null;
  };
  catalog_stats?: {
    materials_processed: number;
    prices_recorded: number;
    errors: string[];
  };
  supplier_id_resolved?: string | null;
  error?: string;
};

/**
 * Helper : gérer les erreurs Edge Function avec extraction du détail
 */
async function handleEdgeFunctionError(error: { message?: string; context?: Response }): Promise<ExtractPdfResult> {
  let detailMsg = error.message || 'Erreur Edge Function';
  try {
    const ctx = error.context;
    if (ctx && typeof ctx.json === 'function') {
      const body = await ctx.json();
      if (body?.error) detailMsg = body.error;
    }
  } catch { /* ignore */ }
  console.error('[CatalogService] Erreur Edge Function:', detailMsg);
  return { success: false, error: detailMsg };
}

/**
 * MODE RAPIDE : Envoie le PDF en base64 directement à l'Edge Function.
 * ⚡ Élimine les round-trips Storage download + Mistral upload + Mistral delete
 *
 * @param pdfBase64 - Le contenu du PDF encodé en base64
 * @param skipCatalog - Si true, ne pas alimenter le catalogue (extraction seule)
 */
export async function callExtractPdfBase64(
  pdfBase64: string,
  skipCatalog = true
): Promise<ExtractPdfResult> {
  try {
    const { data, error } = await supabase.functions.invoke('extract-pdf-data', {
      body: { pdf_base64: pdfBase64, skip_catalog: skipCatalog }
    });

    if (error) return handleEdgeFunctionError(error as { message?: string; context?: Response });
    return data;
  } catch (err: unknown) {
    console.error('[CatalogService] Erreur appel Edge Function (base64):', err);
    return { success: false, error: err instanceof Error ? err.message : 'Erreur inconnue' };
  }
}

// ─── TYPE DE RETOUR EXTRACTION FACTURE ──────────────────────────────────

export type ExtractInvoicePdfResult = {
  success: boolean;
  document_type?: 'invoice';
  extracted?: {
    client_name: string;
    client_address: string;
    client_email: string | null;
    invoice_number: string;
    invoice_date: string;
    due_date: string | null;
    tva_rate: number | null;
    lines: { description: string; quantity: number; unit: string; unit_price: number }[];
    total_ht: number | null;
    total_ttc: number | null;
    project_code: string | null;
    notes: string | null;
    has_signature: boolean;
    validation_mention: string | null;
  };
  error?: string;
};

/**
 * MODE RAPIDE FACTURE : Envoie le PDF en base64 à l'Edge Function
 * avec document_type='invoice' pour extraction structurée des données facture.
 */
export async function callExtractInvoicePdfBase64(
  pdfBase64: string
): Promise<ExtractInvoicePdfResult> {
  try {
    const { data, error } = await supabase.functions.invoke('extract-pdf-data', {
      body: { pdf_base64: pdfBase64, document_type: 'invoice', skip_catalog: true }
    });

    if (error) return handleEdgeFunctionError(error as { message?: string; context?: Response }) as unknown as ExtractInvoicePdfResult;
    return data;
  } catch (err: unknown) {
    console.error('[CatalogService] Erreur extraction facture (base64):', err);
    return { success: false, error: err instanceof Error ? err.message : 'Erreur inconnue' };
  }
}

/**
 * MODE CLASSIQUE : Appeler l'Edge Function avec un file_path Storage.
 * Utilisé pour l'alimentation catalogue au moment de la sauvegarde.
 *
 * @param filePath - Chemin du fichier dans Supabase Storage (bucket bl-pdfs)
 * @param supplierId - ID du fournisseur pour alimenter les prix
 */
export async function callExtractPdfEdgeFunction(
  filePath: string,
  supplierId?: string
): Promise<ExtractPdfResult | null> {
  try {
    const { data, error } = await supabase.functions.invoke('extract-pdf-data', {
      body: { file_path: filePath, supplier_id: supplierId || null }
    });

    if (error) return handleEdgeFunctionError(error as { message?: string; context?: Response });
    return data;
  } catch (err: unknown) {
    console.error('[CatalogService] Erreur appel Edge Function:', err);
    return { success: false, error: err instanceof Error ? err.message : 'Erreur inconnue' };
  }
}
